import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, RotateCcw, AlertTriangle, Copy, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiDelete, apiPost, apiPatch } from '../../api/client';
import type { Event } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { PageHeader, type ActionDef } from '../../components/ui/PageHeader';
import { EventStatusBadge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { ConfirmDialog } from '../../components/ui/Modal';
import { formatDate, formatEventType } from '../../utils/format';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { usePermissions } from '../../hooks/usePermission';
import EditEventModal from '../../components/events/EditEventModal';
import OverviewTab from './tabs/OverviewTab';
import ServicesTab from './tabs/ServicesTab';
import ScalettaTab from './tabs/ScalettaTab';
import CollaboratorsTab from './tabs/CollaboratorsTab';
import PostProductionTab from './tabs/PostProductionTab';
import EconomicaTab from './tabs/EconomicaTab';
import FilesAndNotesTab from './tabs/FilesAndNotesTab';
import ContractTab from './tabs/ContractTab';
import TimelineTab from './tabs/TimelineTab';

const TABS = [
  { id: 'overview',        label: 'Panoramica',       permKey: 'events.overview.view' },
  { id: 'services',        label: 'Servizi',           permKey: 'events.services.view' },
  { id: 'scaletta',        label: 'Scaletta',          permKey: 'events.scaletta.view' },
  { id: 'collaborators',   label: 'Collaboratori',     permKey: 'events.collaborators.view' },
  { id: 'post-production', label: 'Post-Produzione',   permKey: 'events.post-production.view' },
  { id: 'economica',       label: 'Economica',         permKey: 'events.financial' },
  { id: 'files',           label: 'File & Note',       permKey: 'events.files.view' },
  { id: 'contract',        label: 'Contratto',         permKey: 'events.contract.view' },
  { id: 'timeline',        label: 'Timeline',          permKey: 'events.timeline.view' },
];

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canOverview       = hasPermission('events.overview.view');
  const canServices       = hasPermission('events.services.view');
  const canScaletta       = hasPermission('events.scaletta.view');
  const canCollabTab      = hasPermission('events.collaborators.view');
  const canPostProd       = hasPermission('events.post-production.view');
  const canFinancial      = hasPermission('events.financial');
  const canFiles          = hasPermission('events.files.view');
  const canContract       = hasPermission('events.contract.view');
  const canTimeline       = hasPermission('events.timeline.view');

  const tabPermMap: Record<string, boolean> = {
    overview:          canOverview,
    services:          canServices,
    scaletta:          canScaletta,
    collaborators:     canCollabTab,
    'post-production': canPostProd,
    economica:         canFinancial,
    files:             canFiles,
    contract:          canContract,
    timeline:          canTimeline,
  };

  const visibleTabs = TABS.filter(t => tabPermMap[t.id] ?? true);
  const [activeTab, setActiveTab] = useState('overview');
  const safeTab = visibleTabs.some(t => t.id === activeTab) ? activeTab : (visibleTabs[0]?.id ?? 'overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCloneConfirm, setShowCloneConfirm] = useState(false);
  const isAdmin = useIsAdmin();

  const { data: event, isLoading } = useQuery<Event>({
    queryKey: ['event', id],
    queryFn: () => apiGet<Event>(`/events/${id}`),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/events/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Evento spostato nel cestino');
      navigate('/events');
    },
    onError: () => toast.error('Errore durante l\'eliminazione'),
  });

  const restoreMutation = useMutation({
    mutationFn: () => apiPost(`/events/${id}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Evento ripristinato');
    },
    onError: () => toast.error('Errore durante il ripristino'),
  });

  const cloneMutation = useMutation({
    mutationFn: () => apiPost<{ id: string }>(`/events/${id}/clone`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Evento duplicato — imposta la data per completarlo');
      navigate(`/events/${data.id}`);
    },
    onError: () => toast.error('Errore durante la duplicazione'),
  });

  const [showEditModal, setShowEditModal] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (body: Partial<Event>) => apiPatch<Event>(`/events/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setShowEditModal(false);
      toast.success('Evento aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <Skeleton height={40} width="50%" />
        <Skeleton height={200} />
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
        <p>Evento non trovato.</p>
        <Button variant="ghost" onClick={() => navigate('/events')} style={{ marginTop: '1rem' }}>
          ← Torna agli eventi
        </Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        backTo="/events"
        title={event.title}
        badge={<EventStatusBadge status={event.status} size="md" />}
        onTitleChange={(t) => updateMutation.mutate({ title: t } as Partial<Event>)}
        metadata={
          <>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{formatEventType(event.eventType)}</span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>·</span>
            <span className="text-safe" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {formatDate(event.eventDate, 'EEEE d MMMM yyyy')}
              {event.startTime && <> — <strong>{event.startTime}</strong></>}
            </span>
            {event.location && (
              <>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>·</span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{event.location}</span>
              </>
            )}
          </>
        }
        actions={
          event.deletedAt ? [] : [
            ...(isAdmin && !event.deletedAt ? [{ label: 'Modifica', icon: <Pencil size={14} />, onClick: () => setShowEditModal(true) }] : []),
            ...(isAdmin ? [{ label: 'Duplica', icon: <Copy size={14} />, onClick: () => setShowCloneConfirm(true) }] : []),
            ...(isAdmin ? [{ label: 'Elimina', icon: <Trash2 size={14} />, onClick: () => setShowDeleteConfirm(true), danger: true }] : []),
          ]
        }
      >
        {event.deletedAt && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', background: 'var(--color-error-highlight)', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-card)', padding: '0.75rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={16} style={{ color: 'var(--color-error)', flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--text-sm)' }}>Questo evento è nel cestino — eliminato il {formatDate(event.deletedAt!, 'd MMMM yyyy')}</span>
            </div>
            {isAdmin && <Button variant="ghost" size="sm" icon={<RotateCcw size={14} />} onClick={() => restoreMutation.mutate()}>Ripristina</Button>}
          </div>
        )}
      </PageHeader>

      <EditEventModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        event={event}
        onSave={async (data) => { try { await updateMutation.mutateAsync(data as Partial<Event>); } catch {} }}
        isSaving={updateMutation.isPending}
      />

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.375rem',
        paddingBottom: '0.5rem',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}>
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 1rem',
              background: safeTab === tab.id ? 'var(--color-primary-highlight)' : 'transparent',
              border: safeTab === tab.id ? '1px solid rgba(79,152,163,0.3)' : '1px solid transparent',
              borderRadius: 'var(--radius-interactive)',
              cursor: 'pointer',
              color: safeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontSize: 'var(--text-sm)',
              fontWeight: safeTab === tab.id ? 600 : 400,
              transition: 'all var(--spring-duration) var(--spring-easing)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {safeTab === 'overview'        && <OverviewTab event={event} onSwitchTab={setActiveTab} />}
      {safeTab === 'services'        && <ServicesTab event={event} />}
      {safeTab === 'scaletta'        && <ScalettaTab event={event} />}
      {safeTab === 'collaborators'   && <CollaboratorsTab event={event} />}
      {safeTab === 'post-production' && <PostProductionTab event={event} />}
      {safeTab === 'economica'       && canFinancial && <EconomicaTab event={event} />}
      {safeTab === 'files'           && <FilesAndNotesTab event={event} />}
      {safeTab === 'contract'        && <ContractTab event={event} />}
      {safeTab === 'timeline'        && <TimelineTab event={event} />}

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Sposta nel cestino"
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        requiresConfirmCheck
        description={
          <>
            Stai per spostare <strong>{event.title}</strong> nel cestino.
            <br /><br />
            L'evento non sarà più visibile nella lista e nel calendario, ma tutti i dati (pagamenti, contratto, file, checklist) resteranno salvati e potrai ripristinarlo in qualsiasi momento dalla vista Cestino.
          </>
        }
      />

      <ConfirmDialog
        open={showCloneConfirm}
        onClose={() => setShowCloneConfirm(false)}
        onConfirm={() => cloneMutation.mutate()}
        title="Duplica evento"
        confirmLabel="Duplica"
        loading={cloneMutation.isPending}
        description={
          <>
            Verrà creata una copia di <strong>{event.title}</strong> con tutti i servizi inclusi.
            <br /><br />
            Il nuovo evento sarà in stato <strong>Preventivo</strong>. Collaboratori, pagamenti, contratto e allegati <strong>non</strong> verranno copiati.
          </>
        }
      />
    </div>
  );
}
